import { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { Camera, Crop, Check } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { compressImage, cropImage } from '@/utils/imageProcessing';
import { logError } from '@/utils/logger';

/**
 * Uploader de photo unique avec recadrage optionnel (react-easy-crop).
 *
 * @param {string} currentUrl - URL actuelle de la photo
 * @param {function(string)} onUploaded - callback appelé avec l'URL uploadée
 * @param {'sm'|'md'|'lg'} size - taille de la miniature
 * @param {boolean} enableCrop - active le dialog de recadrage (défaut: true)
 * @param {'round'|'rect'} cropShape - forme du recadrage (défaut: 'round')
 * @param {number} aspect - ratio de recadrage (défaut: 1 pour carré/rond)
 */
const PhotoUpload = ({
  currentUrl,
  onUploaded,
  size = 'md',
  enableCrop = true,
  cropShape = 'round',
  aspect = 1,
}) => {
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(currentUrl || null);
  const [cropSrc, setCropSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [areaPixels, setAreaPixels] = useState(null);

  const sizeClasses = {
    sm: 'h-12 w-12',
    md: 'h-20 w-20',
    lg: 'h-28 w-28',
  };

  const onCropComplete = useCallback((_, pixels) => setAreaPixels(pixels), []);

  const uploadBlob = async (blob, name = 'photo.jpg') => {
    setUploading(true);
    try {
      const formData = new FormData();
      const file = blob instanceof File ? blob : new File([blob], name, { type: blob.type || 'image/jpeg' });
      formData.append('file', file);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/upload/photo`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      if (response.ok) {
        const result = await response.json();
        const photoUrl = `${process.env.REACT_APP_BACKEND_URL}/api/files/${result.file_id}`;
        setPreviewUrl(photoUrl);
        onUploaded(photoUrl);
        toast.success('Photo uploadée');
      } else {
        toast.error('Erreur lors de l\'upload');
        setPreviewUrl(currentUrl || null);
      }
    } catch (error) {
      logError('Upload failed:', error);
      toast.error('Erreur lors de l\'upload');
      setPreviewUrl(currentUrl || null);
    } finally {
      setUploading(false);
    }
  };

  const handleFilePick = async (e) => {
    const raw = e.target.files?.[0];
    e.target.value = '';
    if (!raw) return;

    if (enableCrop) {
      // Ouvre le dialog de recadrage
      const reader = new FileReader();
      reader.onload = () => {
        setCropSrc(reader.result);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setAreaPixels(null);
      };
      reader.readAsDataURL(raw);
    } else {
      // Upload direct avec compression
      const file = await compressImage(raw).catch(() => raw);
      setPreviewUrl(URL.createObjectURL(file));
      await uploadBlob(file, file.name);
    }
  };

  const confirmCrop = async () => {
    if (!cropSrc || !areaPixels) return;
    try {
      const cropped = await cropImage(cropSrc, areaPixels);
      const compressed = await compressImage(
        new File([cropped], 'photo.jpg', { type: 'image/jpeg' })
      );
      setCropSrc(null);
      await uploadBlob(compressed, 'photo.jpg');
    } catch (err) {
      logError('crop failed', err);
      toast.error('Erreur lors du recadrage');
    }
  };

  const skipCrop = async () => {
    if (!cropSrc) return;
    try {
      const resp = await fetch(cropSrc);
      const blob = await resp.blob();
      const compressed = await compressImage(
        new File([blob], 'photo.jpg', { type: blob.type || 'image/jpeg' })
      );
      setCropSrc(null);
      await uploadBlob(compressed, 'photo.jpg');
    } catch (err) {
      logError('skip crop failed', err);
      toast.error('Erreur lors de l\'upload');
    }
  };

  return (
    <div className="flex flex-col items-center gap-2">
      <label className="relative cursor-pointer group" data-testid="photo-upload">
        <div
          className={`${sizeClasses[size]} rounded-full overflow-hidden border-2 border-[#334155] group-hover:border-[#2563EB] transition-colors bg-[#1E293B] flex items-center justify-center`}
        >
          {previewUrl ? (
            <img src={previewUrl} alt="Photo" className="h-full w-full object-cover" />
          ) : (
            <Camera size={size === 'lg' ? 32 : size === 'md' ? 24 : 16} className="text-[#94A3B8]" />
          )}
          <div className="absolute inset-0 rounded-full bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
            <Camera size={20} className="text-white" />
          </div>
        </div>
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={handleFilePick}
          disabled={uploading}
          className="hidden"
        />
      </label>
      <span className="text-xs text-[#94A3B8]">
        {uploading ? 'Upload...' : 'Cliquez pour changer'}
      </span>

      {/* Dialog recadrage */}
      <Dialog open={!!cropSrc} onOpenChange={(o) => !o && setCropSrc(null)}>
        <DialogContent className="bg-[#0F172A] border-[#1E293B] text-[#F8FAFC] max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Crop size={18} /> Recadrer la photo
            </DialogTitle>
          </DialogHeader>
          <div className="relative w-full h-80 bg-black rounded-md overflow-hidden">
            {cropSrc && (
              <Cropper
                image={cropSrc}
                crop={crop}
                zoom={zoom}
                aspect={aspect}
                cropShape={cropShape}
                showGrid={cropShape === 'rect'}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            )}
          </div>
          <div className="flex items-center gap-3 pt-2">
            <label className="text-xs text-[#94A3B8] flex-shrink-0">Zoom</label>
            <input
              type="range"
              min="1"
              max="4"
              step="0.1"
              value={zoom}
              onChange={(e) => setZoom(parseFloat(e.target.value))}
              className="flex-1 accent-[#2563EB]"
              data-testid="photo-crop-zoom"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setCropSrc(null)}
              className="border-[#334155] text-[#F8FAFC] hover:bg-[#1E293B]"
              data-testid="photo-crop-cancel"
            >
              Annuler
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={skipCrop}
              disabled={uploading}
              className="border-[#334155] text-[#F8FAFC] hover:bg-[#1E293B]"
              data-testid="photo-crop-skip"
            >
              Sans recadrage
            </Button>
            <Button
              type="button"
              onClick={confirmCrop}
              disabled={uploading || !areaPixels}
              className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
              data-testid="photo-crop-confirm"
            >
              <Check size={16} className="mr-1" /> {uploading ? 'Envoi...' : 'Valider'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PhotoUpload;
